import { Box, Button, Skeleton, Stack, Tab, Typography } from "@mui/material";
import { FC, useState } from "react";

import TitleCard from "../../Cards/TitleCard";
import ConfigContentRecaps from "../../Config/ConfigContentRecaps/config-content-row-recap";
import OrderStatusIcon from "../../Misc/Status/OrderStatus/order-status";
import Panel, { PanelProps } from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabs from "../PanelTabs";
import OrderItemTabDetails from "./order-item-panel-tab-details";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import React from "react";
import { cacheService, orderService, thumbnailService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import {
  GetCurrencyChar,
  GetOrderProductReadableType,
  OpenLinkInNewTab,
  OrderTypeAlphaColor,
  UserHasAcl,
} from "../../../Utils/helpers";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import ConfigTaskCard from "../../Cards/ConfigTaskCard/stack-task-card";
import OrderMissingAssetCard from "../../Cards/OrderMissingAssetCard/order-missing-asset-card";
import OrderThumbnail from "../../Misc/OrderThumbnail/order-thumbnail";
import ResourceNotification from "../../Notification/resource-notification";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import { useTranslation } from "react-i18next";
import { ShoppingBagOutlined } from "@mui/icons-material";

export interface OrderItemPanelProps extends PanelProps {
  opening?: PanelHeaderProps["opening"];
  itemId: number;
  tab?: number;
}

const OrderItemPanel: FC<OrderItemPanelProps> = ({ onClose, open, opening, itemId, tab = 0, ...otherProps }) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(tab);
  const [isLoading, setLoading] = useState(true);
  const [item, setData] = useState<DbOrderItemInfos>();
  const { modifyObjectByIdToBasket, addPreconfiguredItemToBasket, isBasketLoading } = useBasketActions();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const canCopyItemToBasket = UserHasAcl(AclTypes.aclSuperUser);

  React.useEffect(() => {
    setLoading(true);
    if (itemId > 0 && open) {
      orderService.GetOrderItem(itemId).then(function (response) {
        setData(response.item!);
        setLoading(false);
      });
    }
  }, [itemId, open]);

  let productTypeColor: OrderProductType = !item?.doable ? OrderProductType.TranscodeByProfile : item?.type!;

  return (
    <Panel
      reload={itemId}
      header={
        <PanelHeader
          title={t("panel:title.orderItemDetails")}
          onClose={onClose as PanelHeaderProps["onClose"]}
          opening={opening}
        />
      }
      footerContent={
        <Stack direction="row" flexWrap="wrap" width="100%" gap={1}>
          {canCopyItemToBasket && item && (
            <Button
              startIcon={<ShoppingBagOutlined />}
              onClick={() => addPreconfiguredItemToBasket(item)}
              loading={isBasketLoading}
              sx={{ flex: 1 }}
            >
              {t("panel:orderItemPanel.footer.copyItemToBasket")}
            </Button>
          )}
          <Button
            startIcon={<PrintOutlinedIcon />}
            disabled={item?.status !== OrderStatus.COMPLETED}
            sx={{ flex: 1 }}
            onClick={() =>
              OpenLinkInNewTab(
                orderService.GetServiceUrl() + "/orders/items/" + itemId + "/email/" + cacheService.GetAuthKey()
              )
            }
          >
            {t("common:print")}
          </Button>
          <Button
            startIcon={<EditOutlinedIcon />}
            variant="outlined"
            fullWidth
            onClick={() => modifyObjectByIdToBasket(itemId)}
            disabled={item?.status !== OrderStatus.READY}
          >
            {t("common:editUpperCase")}
          </Button>
        </Stack>
      }
      open={open}
      onClose={onClose}
      {...otherProps}
    >
      {isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={130} />
          <Stack direction="row" spacing={0.5}>
            <Skeleton variant="rounded" height={40} width={146} />
            <Skeleton variant="rounded" height={40} width={146} />
            <Skeleton variant="rounded" height={40} width={146} />
          </Stack>
          <Skeleton variant="rounded" height={15} />
          <Skeleton variant="rounded" height={30} />
          <Skeleton variant="rounded" height={15} width={220} />
          <Stack spacing={10}>
            <Box></Box>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <>
          <Box
            sx={{
              p: 3,
              ml: -2,
              mt: -3,
              mr: -2,
              background: `radial-gradient(59.89% 124.74% at 36.7% 64.33%,${OrderTypeAlphaColor(
                productTypeColor,
                0.1
              )} 0%, ${OrderTypeAlphaColor(productTypeColor, 0)} 100%), ${OrderTypeAlphaColor(productTypeColor, 0.05)}`,
            }}
          >
            <Typography variant="h6" color="text.placeholder" mb={2}>
              {t("panel:body.itemId", { id: item?.id })}
            </Typography>
            <Stack direction="row" alignItems="flex-start" spacing={5}>
              <div>
                <Typography component="h2" fontWeight={600} variant="h5" mb={1.5}>
                  {GetOrderProductReadableType(item?.type!)}
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <ConfigContentRecaps variant="body2" color="text.tier" setting={item?.summarizedInfos!} />
                </Box>
                <OrderStatusIcon status={item?.status!} />
              </div>
              <OrderThumbnail size="large" type={item?.type!} sx={{ flexShrink: 0, ml: "auto !important" }} />
            </Stack>

            {item && !item.doable && <OrderMissingAssetCard key={item.id} item={item} />}
          </Box>

          <PanelTabs value={tabValue} onChange={handleTabChange} aria-label="tabs" variant="fullWidth">
            <Tab label={t("common:configuration")} {...a11yProps(0)} />
            <Tab label={t("common:tasks")} {...a11yProps(1)} />
            <Tab label={t("common:activities")} {...a11yProps(2)} />
          </PanelTabs>
          <Box mt={2}>
            <PanelTabContent index={0} activePanelIndex={tabValue} id="order-tabpanel">
              <TitleCard
                poster={thumbnailService.GetObjectPosterUrl(item?.relatedTitleId)}
                title={item?.titleName!}
                titleId={item?.relatedTitleId!}
                price={
                  item?.price !== null
                    ? item?.price?.price?.toString() + " " + GetCurrencyChar(item?.price?.currency!)
                    : ""
                }
                sx={{ mb: 2 }}
              />
              <OrderItemTabDetails item={item!} />
            </PanelTabContent>
            <PanelTabContent index={1} activePanelIndex={tabValue} id="order-tabpanel">
              {item?.stack?.tasks?.map((task, index) => (
                <ConfigTaskCard
                  title={task.type!.toString()}
                  taskType={task.type!}
                  taskKind={task.kind!}
                  ressourceId={task.resourceId!}
                  infos={task.name!}
                  progress={task.progress!}
                  status={task.status!}
                  defaultExpanded={task.status !== OrderStatus.COMPLETED}
                  startDate={task.createdAt}
                  endDate={task.endedAt}
                  key={index}
                />
              ))}
              {(!item?.stack?.tasks || item?.stack?.tasks?.length === 0) && (
                <>
                  <Box p={2}>
                    <Typography>{t("panel:orderItemPanel.body.noTasks")}</Typography>
                  </Box>
                </>
              )}
            </PanelTabContent>
            <PanelTabContent index={2} activePanelIndex={tabValue} id="order-tabpanel">
              <ResourceNotification
                resourceKind={ActivityKind.OrderItem}
                resourceId={item?.id}
                enableComments={UserHasAcl(AclTypes.aclActivityAdd)}
              />
            </PanelTabContent>
          </Box>
        </>
      )}
    </Panel>
  );
};

function a11yProps(index: number) {
  return {
    id: `order-tab-${index}`,
    "aria-controls": `order-tabpanel-${index}`,
  };
}

export default OrderItemPanel;
